import { useMemo, useState } from 'react'
import { useLiveQuery } from 'dexie-react-hooks'
import { db, type OptionSet, type OrderItem } from '../lib/db'
import { Sheet } from './Sheet'

/**
 * Ghi chú cho một dòng món: "ít đường", "nhiều đá", "không hành".
 *
 * Bấm chip là xong — người bán đang đứng trước mặt khách, không có thời gian gõ. Ô gõ tay
 * ở dưới chỉ để dành cho câu lạ mà không bộ nào có sẵn.
 *
 * Ghi chú lưu thành một chuỗi phẳng vào `OrderItem.note`, thứ vốn đã đồng bộ lên máy chủ
 * và đã in ra bill từ đầu — chỉ là chưa bao giờ có chỗ để nhập vào.
 */

/** Ngăn giữa các phần của ghi chú. Cũng là thứ dùng để đọc ngược ra chip nào đang chọn. */
const SEP = ', '

/**
 * Đọc ngược chuỗi ghi chú ra thành chip đã chọn và phần gõ tay.
 *
 * Đọc ngược chứ không lưu riêng cấu trúc: dòng hàng đã có đúng một ô `note` và ô ấy đi
 * khắp nơi — lên máy chủ, ra máy in, vào hoá đơn điện tử. Thêm một ô nữa chỉ để nhớ chip
 * nào được bấm là thêm một thứ có thể lệch với cái đang in trên giấy.
 */
function parseNote(note: string | null, known: Set<string>) {
  const parts = (note ?? '')
    .split(SEP)
    .map((p) => p.trim())
    .filter(Boolean)

  const picked = new Set<string>()
  const rest: string[] = []

  for (const p of parts) {
    if (known.has(p)) picked.add(p)
    else rest.push(p)
  }

  return { picked, free: rest.join(SEP) }
}

interface Props {
  item: OrderItem
  onSave: (note: string | null) => void | Promise<void>
  onClose: () => void
}

export function NoteSheet({ item, onSave, onClose }: Props) {
  // Món nào thì hiện bộ ghi chú của món đó. Bán nhanh không gắn sản phẩm thì không có bộ
  // nào — vẫn gõ tay được.
  const sets = useLiveQuery(async () => {
    const all = await db.optionSets.orderBy('sortOrder').toArray()
    if (!item.productId) return [] as OptionSet[]

    const p = await db.products.get(item.productId)
    const want = new Set(p?.optionSetIds ?? [])
    return all.filter((s) => want.has(s.id))
  }, [item.productId], [] as OptionSet[])

  const known = useMemo(() => new Set(sets.flatMap((s) => s.choices)), [sets])
  const start = useMemo(() => parseNote(item.note, known), [item.note, known])

  const [picked, setPicked] = useState<Set<string>>(start.picked)
  const [free, setFree] = useState(start.free)

  function toggle(set: OptionSet, choice: string) {
    setPicked((prev) => {
      const next = new Set(prev)

      if (next.has(choice)) {
        next.delete(choice)
        return next
      }

      // Trong một bộ chỉ chọn được một: "ít đường" và "nhiều đường" cùng lúc thì pha kiểu gì.
      for (const other of set.choices) next.delete(other)
      next.add(choice)
      return next
    })
  }

  async function save() {
    // Giữ đúng thứ tự các bộ, để hai lần ghi cùng nội dung ra cùng một chuỗi.
    const chips = sets.flatMap((s) => s.choices.filter((c) => picked.has(c)))
    const text = [...chips, free.trim()].filter(Boolean).join(SEP)

    await onSave(text || null)
    onClose()
  }

  return (
    <Sheet title={item.name} onClose={onClose}>
      {sets.length === 0 && (
        <p className="hint">
          Món này chưa gắn bộ ghi chú nào. Vào Sản phẩm để tích bộ cho món, hoặc gõ thẳng
          vào ô bên dưới.
        </p>
      )}

      {sets.map((s) => (
        <div key={s.id} className="field">
          <span>{s.name}</span>
          <div className="chip-row">
            {s.choices.map((c) => (
              <button
                key={c}
                type="button"
                className="chip"
                aria-pressed={picked.has(c)}
                onClick={() => toggle(s, c)}
              >
                {c}
              </button>
            ))}
          </div>
        </div>
      ))}

      <label className="field">
        <span>Ghi chú khác</span>
        <input
          value={free}
          onChange={(e) => setFree(e.target.value)}
          placeholder="Ví dụ: không lấy ống hút"
          maxLength={150}
        />
      </label>

      <div className="sheet-actions">
        {(picked.size > 0 || free.trim()) && (
          <button
            type="button"
            className="btn btn-danger-ghost"
            onClick={() => {
              setPicked(new Set())
              setFree('')
            }}
          >
            Xoá hết
          </button>
        )}
        <button type="button" className="btn btn-brand grow" onClick={save}>
          Xong
        </button>
      </div>
    </Sheet>
  )
}
